import classNames from "@/utils/classNames";
import {
    forwardRef,
    TextareaHTMLAttributes,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";

const DEFAULT_MAX_LENGTH = 255;
const DEFAULT_MAX_LENGTH_NOTICE_THRESHOLD = 80;

export default forwardRef(function TextAreaInput(
    {
        className = "",
        containerClassName = "",
        isFocused = false,
        maxLength = DEFAULT_MAX_LENGTH,
        characterCountColor = "text-brand-gray",
        height = 24,
        alwaysShowCharacterCount = false,
        ...props
    }: TextareaHTMLAttributes<HTMLTextAreaElement> & {
        containerClassName?: string;
        isFocused?: boolean;
        maxLength?: number;
        height?: number | null;
        characterCountColor?: string;
        alwaysShowCharacterCount?: boolean;
    },
    ref,
) {
    const localRef = useRef<HTMLTextAreaElement>(null);

    useImperativeHandle(ref, () => ({
        focus: () => localRef.current?.focus(),
    }));

    useEffect(() => {
        if (isFocused) {
            localRef.current?.focus();
        }

        // There might be a slight chance that the ref won't be available immediately,
        // But I don't want to put it as dependency on the value because it will cause duplicated renders.
        if (localRef.current?.value) {
            checkMaxLength(localRef.current?.value);
        }
    }, []);

    const [showMaxLength, setShowMaxLength] = useState(false);
    const [characterCount, setCharacterCount] = useState(0);

    const checkMaxLength = (value: string) => {
        setCharacterCount(value.length);
        const percentage = (value.length / (maxLength ?? 0)) * 100;

        if (maxLength && percentage >= DEFAULT_MAX_LENGTH_NOTICE_THRESHOLD) {
            setShowMaxLength(true);
        } else {
            setShowMaxLength(false);
        }
    };

    const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        props.onChange?.(e);
        checkMaxLength(e.target.value);
    };

    return (
        <div className={classNames("flex flex-col", containerClassName)}>
            <textarea
                {...props}
                maxLength={maxLength}
                onChange={onChange}
                className={classNames(
                    "resize-none rounded-md outline-offset-1 border border-brand-border p-2 text-brand-gray shadow-xs focus:outline-brand-red focus:border-brand-red",
                    height !== null ? `!h-${height}` : "",
                    props.disabled ? "bg-gray-100" : "bg-white",
                    className,
                )}
                ref={localRef}
            />
            {(showMaxLength || alwaysShowCharacterCount) && (
                <p
                    className={classNames(
                        "self-end text-xs pt-1",
                        characterCountColor,
                    )}
                >
                    {characterCount}/{maxLength}
                </p>
            )}
        </div>
    );
});
